import React, { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getMyNotificationPreferences,
  updateMyNotificationPreferences,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { BellSimple, EnvelopeSimple, Receipt, Ticket, FileText, At, CheckSquare, Warning, Info, ThumbsUp, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Categories the user can mute. Keys must match backend's entity_type / notification_type values.
const CATEGORIES = [
  { key: 'invoice', icon: Receipt, es: 'Facturas vencidas', en: 'Overdue invoices' },
  { key: 'ticket', icon: Ticket, es: 'Nuevos tickets', en: 'New tickets' },
  { key: 'quotation', icon: FileText, es: 'Nuevas cotizaciones', en: 'New quotations' },
  { key: 'task_mention', icon: At, es: 'Menciones en tareas', en: 'Task mentions' },
  { key: 'approval', icon: ThumbsUp, es: 'Solicitudes de aprobación', en: 'Approval requests' },
  { key: 'reminder', icon: BellSimple, es: 'Recordatorios', en: 'Reminders' },
  { key: 'warning', icon: Warning, es: 'Advertencias', en: 'Warnings' },
  { key: 'info', icon: Info, es: 'Información general', en: 'General info' },
];

const EMPTY = {
  email_notifications_enabled: true,
  muted_categories_email: [],
  muted_categories_inapp: [],
};

export default function NotificationPreferencesPage() {
  const { t } = useLanguage();
  const isSpanish = (t('language') || '').toLowerCase() !== 'english';
  const [prefs, setPrefs] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchPrefs = async () => {
    try {
      const { data } = await getMyNotificationPreferences();
      setPrefs({
        email_notifications_enabled: data?.email_notifications_enabled ?? true,
        muted_categories_email: data?.muted_categories_email || [],
        muted_categories_inapp: data?.muted_categories_inapp || [],
      });
    } catch (error) {
      toast.error(
        isSpanish
          ? 'No se pudieron cargar las preferencias'
          : 'Failed to load preferences',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isMuted = (listKey, cat) => (prefs[listKey] || []).includes(cat);

  // In this UI a row with "Email ON + In-app ON" means NOT muted in either channel.
  // Internally we flip to muted list.
  const toggleMute = (listKey, cat, nextEnabled) => {
    setPrefs((p) => {
      const current = new Set(p[listKey] || []);
      if (nextEnabled) {
        current.delete(cat); // enabling = remove from muted
      } else {
        current.add(cat); // disabling = add to muted
      }
      return { ...p, [listKey]: Array.from(current) };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await updateMyNotificationPreferences({
        email_notifications_enabled: !!prefs.email_notifications_enabled,
        muted_categories_email: prefs.muted_categories_email,
        muted_categories_inapp: prefs.muted_categories_inapp,
      });
      setPrefs({
        email_notifications_enabled: data?.email_notifications_enabled ?? prefs.email_notifications_enabled,
        muted_categories_email: data?.muted_categories_email || prefs.muted_categories_email,
        muted_categories_inapp: data?.muted_categories_inapp || prefs.muted_categories_inapp,
      });
      toast.success(isSpanish ? 'Preferencias guardadas' : 'Preferences saved');
    } catch (error) {
      toast.error(
        error.response?.data?.detail || (isSpanish ? 'Error al guardar' : 'Failed to save'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title={isSpanish ? 'Preferencias de notificación' : 'Notification Preferences'}>
      <div className="max-w-4xl space-y-6" data-testid="notification-preferences-page">
        {/* Header card */}
        <div className="valhalla-card p-6 bg-gradient-to-br from-[#3AB8D6]/10 to-transparent border-[#3AB8D6]/30">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-[#3AB8D6]/20 text-[#3AB8D6]">
              <BellSimple weight="duotone" className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h2 className="font-['Outfit'] text-xl font-bold text-[#F3F4F6]">
                {isSpanish ? 'Tus preferencias de notificación' : 'Your notification preferences'}
              </h2>
              <p className="text-sm text-[#9CA3AF] mt-1 leading-relaxed">
                {isSpanish
                  ? 'Controla qué categorías quieres recibir por correo y cuáles ver en la campana de la app. Los ajustes se aplican solo a tu usuario.'
                  : 'Control which categories you receive by email and which show in the in-app bell. Settings only apply to your user.'}
              </p>
            </div>
          </div>
        </div>

        {/* Master email toggle */}
        <div className="valhalla-card p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <EnvelopeSimple weight="duotone" className="w-5 h-5 text-[#E2B05E]" />
              <div>
                <h3 className="font-semibold text-[#F3F4F6]">
                  {isSpanish
                    ? 'Recibir notificaciones por correo'
                    : 'Receive email notifications'}
                </h3>
                <p className="text-sm text-[#9CA3AF] mt-1">
                  {isSpanish
                    ? 'Si lo desactivas, no recibirás ningún correo (las notificaciones in-app seguirán funcionando).'
                    : 'If off, you won\'t receive any email (in-app notifications still work).'}
                </p>
              </div>
            </div>
            <Switch
              checked={prefs.email_notifications_enabled}
              disabled={loading}
              onCheckedChange={(v) =>
                setPrefs((p) => ({ ...p, email_notifications_enabled: v }))
              }
              data-testid="email-global-switch"
            />
          </div>
        </div>

        {/* Category list */}
        <div className="valhalla-card overflow-hidden">
          <div className="p-4 border-b border-[#242E42] flex items-center gap-2">
            <CheckSquare weight="duotone" className="w-5 h-5 text-[#3AB8D6]" />
            <h3 className="font-semibold text-[#F3F4F6]">
              {isSpanish ? 'Categorías' : 'Categories'}
            </h3>
          </div>
          <div className="divide-y divide-[#242E42]">
            <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#6B7280]">
              <span>{isSpanish ? 'Categoría' : 'Category'}</span>
              <span className="w-16 text-center">Email</span>
              <span className="w-16 text-center">In-app</span>
            </div>
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const emailEnabled = !isMuted('muted_categories_email', c.key);
              const inAppEnabled = !isMuted('muted_categories_inapp', c.key);
              return (
                <div
                  key={c.key}
                  className="grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-4 items-center hover:bg-[#0B0E14]/30 transition-colors"
                  data-testid={`category-row-${c.key}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 rounded-lg bg-[#151B26] text-[#9CA3AF] flex-shrink-0">
                      <Icon weight="duotone" className="w-4 h-4" />
                    </div>
                    <span className="text-sm text-[#F3F4F6] truncate">
                      {isSpanish ? c.es : c.en}
                    </span>
                  </div>
                  <div className="w-16 flex justify-center">
                    <Switch
                      checked={emailEnabled}
                      disabled={loading || !prefs.email_notifications_enabled}
                      onCheckedChange={(v) => toggleMute('muted_categories_email', c.key, v)}
                      data-testid={`email-switch-${c.key}`}
                    />
                  </div>
                  <div className="w-16 flex justify-center">
                    <Switch
                      checked={inAppEnabled}
                      disabled={loading}
                      onCheckedChange={(v) => toggleMute('muted_categories_inapp', c.key, v)}
                      data-testid={`inapp-switch-${c.key}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={loading || saving}
            className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14] font-semibold"
            data-testid="prefs-save-btn"
          >
            <FloppyDisk className="w-4 h-4 mr-2" weight="duotone" />
            {saving ? (isSpanish ? 'Guardando...' : 'Saving...') : isSpanish ? 'Guardar preferencias' : 'Save preferences'}
          </Button>
        </div>
      </div>
    </Layout>
  );
}
